/**
 * @file woundHazardParameters.js
 * @version 1.0.0
 * @date October 10, 2026
 *
 * @description
 * Parameters of the wound-type hazard model (docs/documentation/SPEC_WOUND_HAZARD.md sections 2-14).
 * CONSTANTS ONLY (APP_RULES 2.1): the calculations live in calculationEngine/modules/woundHazardModule.js.
 *
 * STATUS: every value is a PLACEHOLDER proposed on 2026-10-10 and reviewed for plausibility by
 * Dr. Tatar Radu MD; none is traced to a published source yet. Where a published figure motivated
 * the structure it is named in `basis` (Eastridge et al. 2011, J Trauma 71(1 Suppl):S4-8 for the share
 * of hemorrhage and brain injury in died-of-wounds cases). `listHazardPlaceholders()` returns every
 * placeholder so the Assumptions panel can show them (APP_RULES: no silent defaults).
 *
 * COMPONENT KINDS (t in minutes for 'saturating' and 'controllable', in hours for 'weibull'):
 *   saturating    P(t) = p x m x (1 - exp(-t / tau))
 *   controllable  P(t) = p x m x [ eff x (1 - exp(-min(t, tc) / tau)) + (1 - eff) x (1 - exp(-t / tau)) ]
 *                 tc = time control is achieved (care.controlTimeMin, at least minControlMin)
 *                 eff is multiplied by care.trainingScale (capped at 1) when scaleEfficacyWithTraining is set
 *   weibull       P(t) = p x m x (1 - exp(-(max(0, t - t0) / eta)^k))
 * m is the product of the care factors whose flag is set in the care input.
 * Total for a region: M(t) = 1 - product(1 - P_i(t)).
 */

export const WOUND_HAZARD_STATUS = Object.freeze({
  PLACEHOLDER: 'PLACEHOLDER',
  SOURCED: 'SOURCED'
});

/** Care flags the model understands. A flag absent from the care input means "not in place". */
export const CARE_FLAGS = Object.freeze({
  RESUSCITATION: 'resuscitation',   // blood, TXA, fluids, oxygen, hemostatic packing
  ANTIBIOTICS: 'antibiotics',
  DRESSING: 'dressing',             // wound care / debridement
  FIELD_CARE: 'fieldCare',          // general field care for chest injury
  BURN_CARE: 'burnCare'             // burn-specific care and wound management
});

const PLACEHOLDER_NOTE = 'Placeholder proposed 2026-10-10 (docs/documentation/SPEC_WOUND_HAZARD.md); no published source yet.';

export const WOUND_HAZARD_REGIONS = Object.freeze(['extremity', 'thorax', 'abdomen', 'head_neck', 'burns', 'multi']);

export const WOUND_HAZARD_COMPONENTS = {
  extremity: [
    { id: 'extremity_bleeding', kind: 'controllable', p: 0.50, tauMin: 10, eff: 0.90, minControlMin: 0,
      scaleEfficacyWithTraining: true,   // tourniquet success follows first-aid training (care.trainingScale)
      factors: {}, status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Tourniquet control; ' + PLACEHOLDER_NOTE },
    { id: 'extremity_late', kind: 'weibull', p: 0.15, t0H: 24, etaH: 96, k: 2.0,
      factors: { antibiotics: 0.4, dressing: 0.7, resuscitation: 0.8 },
      status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Infection and ischemia over about 4 days; ' + PLACEHOLDER_NOTE },
    { id: 'extremity_noncompressible', kind: 'saturating', p: 0.10, tauMin: 120,
      factors: { resuscitation: 0.7 }, status: WOUND_HAZARD_STATUS.PLACEHOLDER,
      basis: 'Junctional or non-compressible bleeding, arrival in extremis (Eastridge 2011: junctional 21% of hemorrhage deaths, 51% in extremis); ' + PLACEHOLDER_NOTE }
  ],
  thorax: [
    { id: 'thorax_acute', kind: 'controllable', p: 0.35, tauMin: 45, eff: 0.70, minControlMin: 0,
      factors: {}, status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Tension pneumothorax and massive hemothorax, decompression or seal; ' + PLACEHOLDER_NOTE },
    { id: 'thorax_continuing', kind: 'weibull', p: 0.25, t0H: 0, etaH: 36, k: 1.5,
      factors: { fieldCare: 0.9 }, status: WOUND_HAZARD_STATUS.PLACEHOLDER,
      basis: 'Continuing hemothorax and respiratory failure; field care barely helps, only surgery stops it (Dr. Tatar review); ' + PLACEHOLDER_NOTE }
  ],
  abdomen: [
    { id: 'abdomen_vascular', kind: 'saturating', p: 0.08, tauMin: 15, factors: {},
      status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Major vascular injury, mostly before arrival; ' + PLACEHOLDER_NOTE },
    { id: 'abdomen_hemorrhage', kind: 'saturating', p: 0.30, tauMin: 90, factors: { resuscitation: 0.7 },
      status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Non-compressible hemorrhage, stopped only by surgery; ' + PLACEHOLDER_NOTE },
    { id: 'abdomen_sepsis', kind: 'weibull', p: 0.35, t0H: 6, etaH: 48, k: 1.8, factors: { antibiotics: 0.6 },
      status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Peritonitis and sepsis from bowel injury; ' + PLACEHOLDER_NOTE }
  ],
  head_neck: [
    { id: 'head_immediate', kind: 'saturating', p: 0.25, tauMin: 8, factors: {},
      status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Immediate non-survivable brain injury (Eastridge 2011: brain injury 83% of non-survivable DOW); ' + PLACEHOLDER_NOTE },
    { id: 'head_slow_nonsurvivable', kind: 'saturating', p: 0.10, tauMin: 180, factors: {},
      status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Slower non-survivable brain injury over hours; ' + PLACEHOLDER_NOTE },
    { id: 'head_airway', kind: 'controllable', p: 0.15, tauMin: 20, eff: 0.60, minControlMin: 0, factors: {},
      status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Airway compromise and neck vascular bleeding; ' + PLACEHOLDER_NOTE },
    { id: 'head_secondary', kind: 'weibull', p: 0.20, t0H: 2, etaH: 24, k: 1.3, factors: { resuscitation: 0.7 },
      status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Secondary brain injury (hypoxia, hypotension, pressure); ' + PLACEHOLDER_NOTE }
  ],
  burns: [
    { id: 'burns_nonsurvivable', kind: 'saturating', p: 0.08, tauMin: 30, factors: {},
      status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Extensive burns, not survivable; ' + PLACEHOLDER_NOTE },
    { id: 'burns_airway', kind: 'controllable', p: 0.15, tauMin: 90, eff: 0.60, minControlMin: 60, factors: {},
      status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Inhalation injury and airway edema; skilled airway control not before 60 min; ' + PLACEHOLDER_NOTE },
    { id: 'burns_shock', kind: 'saturating', p: 0.20, tauMin: 480, factors: { resuscitation: 0.5 },
      status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Burn shock over about 8 h, early fluids; ' + PLACEHOLDER_NOTE },
    { id: 'burns_sepsis', kind: 'weibull', p: 0.30, t0H: 48, etaH: 120, k: 2.0, factors: { burnCare: 0.7 },
      status: WOUND_HAZARD_STATUS.PLACEHOLDER, basis: 'Sepsis and multi-organ failure; ' + PLACEHOLDER_NOTE }
  ]
};

/** Multi-region casualties: two regions of the pair-mix, hazards added and multiplied by the synergy factor. */
export const MULTI_REGION_PARAMETERS = {
  // Regions that can combine, in the order used to read the wound distribution (burns excluded).
  pairRegions: ['extremity', 'thorax', 'abdomen', 'head_neck'],
  synergy: 1.3,
  status: WOUND_HAZARD_STATUS.PLACEHOLDER,
  basis: 'Lethal triad (hypothermia, acidosis, coagulopathy) synergy; ' + PLACEHOLDER_NOTE
};

/**
 * T2 (Delayed) casualties are time-based, not a fixed share of T1 (Dr. Tatar, 2026-10-10):
 * a T2 casualty stays stable for `stableWindowHours` with care, then follows the T1 curve scaled by
 * `severityShare`: M_T2(t) = severityShare x M_T1(max(0, t - stableWindowHours)).
 * Individual variation (exact wound place, organ damage, patient status) is deliberately not modelled.
 */
export const T2_TIME_TRANSFORM = {
  severityShare: 0.4,
  stableWindowHours: 6,
  status: WOUND_HAZARD_STATUS.PLACEHOLDER,
  basis: 'stableWindowHours 6: a T2 casualty can stay stable about 6 h with care; an abdominal wound is infected after 6 h and may become T1 by 12 h (Dr. Tatar, 2026-10-10). ' +
    'severityShare 0.4 echoes the unverified T2 multiplier in woundTypeDistribution.js. ' + PLACEHOLDER_NOTE
};

/**
 * Facility at which a casualty counts as "arrived" for the DOW definition (Mabry 2015, Eastridge 2011:
 * died after reaching a medical treatment facility). Role 2 is chosen so the result is comparable with the
 * US figures, which count deaths after reaching a surgical-resuscitative facility. PLACEHOLDER: decision pending.
 */
export const DOW_ARRIVAL = {
  facility: 'ROLE2_BASIC',
  status: WOUND_HAZARD_STATUS.PLACEHOLDER,
  basis: 'Arrival facility for the DOW split; ' + PLACEHOLDER_NOTE
};

/**
 * How the two composite care flags follow from the three supply-based ones. PLACEHOLDER derivations:
 * chest and general field care need fluids/blood at hand; burn care needs fluids, antibiotics and dressings.
 */
export const CARE_FLAG_DERIVATION = {
  fieldCare: (resuscitation) => resuscitation === true,
  burnCare: (resuscitation, antibiotics, dressing) => resuscitation === true && antibiotics === true && dressing === true,
  status: WOUND_HAZARD_STATUS.PLACEHOLDER,
  basis: 'Composite care flags derived from supply flags; ' + PLACEHOLDER_NOTE
};

/**
 * Hold time (hours) before evacuation can start, per evacuation paradigm, added to arrival and surgery times in the
 * hazard model so that contested and denied environments act through the curve (Dr. Tatar, 2026-10-10).
 * The transit difference alone (a few minutes to about half an hour) is far too small. Basis: Gurney et al. 2025
 * (Military Review, July-August 2025) plan prolonged casualty care of 3-36 h at Role 1 and 12-48 h at Role 2 when
 * evacuation is not possible. The values below are the LOWER ENDS of those ranges, so they are conservative.
 * PLACEHOLDER: the paradigm-to-range assignment is mine and needs Dr. Tatar's review.
 */
export const PARADIGM_HOLD_HOURS = {
  PERMISSIVE: 0,
  CONTESTED: 3,
  DENIED: 12,
  status: WOUND_HAZARD_STATUS.PLACEHOLDER,
  basis: 'Lower ends of the PCC ranges in Gurney et al. 2025 (Role 1: 3-36 h, Role 2: 12-48 h); ' + PLACEHOLDER_NOTE
};

/** Upper limit (hours) accepted for a planner's evacuation hold time. */
export const HOLD_HOURS_LIMIT = 168;

/**
 * Minimum interval between arrival at the DOW arrival facility and the start of surgery (triage, damage control
 * resuscitation, theatre preparation). Without it, a forward surgical team at the arrival facility would mean surgery
 * at the moment of arrival and zero DOW. Same value as the calculator's TREATMENT_TIMES.ROLE2_BASIC (30 min, advanced
 * stabilization and damage control resuscitation). Queue wait behind other casualties is NOT included: the surgical
 * queue DOW from the resource module is not wired to DOW anywhere (open item). PLACEHOLDER.
 */
export const PREOP_INTERVAL_MIN = {
  minutes: 30,
  status: WOUND_HAZARD_STATUS.PLACEHOLDER,
  basis: 'Equals TREATMENT_TIMES.ROLE2_BASIC in casualtyCalculator; ' + PLACEHOLDER_NOTE
};

/**
 * A forward resuscitation team (FRT) is a planner's choice and always helps, but with blood short a clinician's
 * options change the outcome only slightly (Dr. Tatar, 2026-10-10). Share of the FRT benefit realised when the
 * blood alert level shows a shortage. PLACEHOLDER.
 */
export const FRT_BENEFIT_WITHOUT_BLOOD = {
  share: 0.3,
  status: WOUND_HAZARD_STATUS.PLACEHOLDER,
  basis: 'FRT benefit realised when blood is short; ' + PLACEHOLDER_NOTE
};

/**
 * Switch for the model. OFF = today's behaviour (default); SHADOW = compute and report the hazard DOW next to
 * the flat one without changing results; ON = the hazard DOW replaces the flat base DOW.
 * Read from scenario.operation_parameters.wound_hazard_mode (Tab 2 will own the control; until then it is set
 * in the saved scenario). An unknown value throws; an absent value takes WOUND_HAZARD_DEFAULT_MODE.
 */
export const WOUND_HAZARD_MODES = Object.freeze({ OFF: 'OFF', SHADOW: 'SHADOW', ON: 'ON' });

/** Mode used when a scenario does not set one. Flipping the model on by default is changing this one value. */
export const WOUND_HAZARD_DEFAULT_MODE = WOUND_HAZARD_MODES.ON;   // default since 2026-10-10 (Dr. Tatar); OFF = flat reference model

export const resolveWoundHazardMode = (value) => {
  if (value === undefined || value === null) return WOUND_HAZARD_DEFAULT_MODE;
  if (!Object.prototype.hasOwnProperty.call(WOUND_HAZARD_MODES, value)) {
    throw new Error(`wound_hazard_mode must be one of ${Object.keys(WOUND_HAZARD_MODES).join(', ')} (got: ${value})`);
  }
  return WOUND_HAZARD_MODES[value];
};

/** Every placeholder in this file, for the Assumptions panel. */
export const listHazardPlaceholders = () => {
  const out = [];
  Object.entries(WOUND_HAZARD_COMPONENTS).forEach(([region, comps]) => {
    comps.forEach((c) => {
      if (c.status === WOUND_HAZARD_STATUS.PLACEHOLDER) out.push({ id: c.id, region, basis: c.basis });
    });
  });
  [['multi_region_synergy', MULTI_REGION_PARAMETERS], ['t2_time_transform', T2_TIME_TRANSFORM], ['dow_arrival_facility', DOW_ARRIVAL], ['care_flag_derivation', CARE_FLAG_DERIVATION], ['frt_benefit_without_blood', FRT_BENEFIT_WITHOUT_BLOOD], ['paradigm_hold_hours', PARADIGM_HOLD_HOURS], ['preop_interval_min', PREOP_INTERVAL_MIN]]
    .forEach(([id, obj]) => { if (obj.status === WOUND_HAZARD_STATUS.PLACEHOLDER) out.push({ id, region: null, basis: obj.basis }); });
  return out;
};
